import { useEffect, useState } from "react";
import { Download, X, Share, Plus } from "lucide-react";
import { api } from "@/lib/api";

const DISMISS_KEY = "ssp_pwa_dismissed_until";
const DISMISS_DAYS = 7;
const PWA_MARK_KEY = "ssp_pwa_marked_installed";

/**
 * "Add to home screen" prompt for technicians on mobile.
 * - Android/Chrome: captures the `beforeinstallprompt` event so the user gets
 *   the native install dialog from a styled in-app card.
 * - iOS Safari: no API exists, so we show short visual instructions
 *   (Share → Add to Home Screen).
 * - Suppresses itself for 7 days when dismissed, and forever once the app is
 *   already installed (display-mode: standalone).
 */
export default function InstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [show, setShow] = useState(false);
  const [isIos, setIsIos] = useState(false);

  // Best-effort: tell the backend the user has installed (or is running the
  // installed app). Idempotent + auth-gated server-side; we also cache the
  // outcome in localStorage so we don't hammer the endpoint on every load.
  const markInstalled = () => {
    if (localStorage.getItem(PWA_MARK_KEY) === "1") return;
    api.post("/onboarding/mark-pwa-installed", {})
      .then(() => localStorage.setItem(PWA_MARK_KEY, "1"))
      .catch(() => { /* not logged in or offline — try again next boot */ });
  };

  useEffect(() => {
    // Already installed → never prompt, but DO tick the onboarding task.
    const isStandalone =
      (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) ||
      window.navigator.standalone;
    if (isStandalone) {
      markInstalled();
      return;
    }

    // Dismissed within window → skip
    const until = Number(localStorage.getItem(DISMISS_KEY) || 0);
    const dismissedActive = until && Date.now() < until;

    const ua = window.navigator.userAgent;
    const ios = /iPhone|iPad|iPod/.test(ua) && !window.MSStream;
    const isSafari = /^((?!chrome|android).)*safari/i.test(ua);

    const onPrompt = (e) => {
      e.preventDefault();
      setDeferred(e);
      if (!dismissedActive) setShow(true);
    };
    const onInstalled = () => {
      // Fires on Android once the user completes the install dialog
      markInstalled();
      setShow(false);
    };
    // Forced reveal from the onboarding checklist tile
    const onShowRequest = () => {
      // Clear any "maybe later" so the user can act on it now
      localStorage.removeItem(DISMISS_KEY);
      if (ios && isSafari) setIsIos(true);
      setShow(true);
    };

    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    window.addEventListener("pwa:show-prompt", onShowRequest);

    // iOS Safari doesn't fire the event — show our manual hint after a beat
    let iosTimer;
    if (ios && isSafari && !dismissedActive) {
      iosTimer = setTimeout(() => {
        setIsIos(true);
        setShow(true);
      }, 4000);
    }
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
      window.removeEventListener("pwa:show-prompt", onShowRequest);
      if (iosTimer) clearTimeout(iosTimer);
    };
  }, []);

  const dismiss = () => {
    setShow(false);
    localStorage.setItem(
      DISMISS_KEY,
      String(Date.now() + DISMISS_DAYS * 24 * 60 * 60 * 1000)
    );
  };

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    const choice = await deferred.userChoice;
    setShow(false);
    if (choice?.outcome === "accepted") {
      markInstalled();
    } else {
      dismiss();
    }
  };

  if (!show) return null;

  return (
    <div
      className="fixed inset-x-3 bottom-3 z-[60] sm:left-auto sm:right-4 sm:bottom-4 sm:max-w-sm"
      data-testid="pwa-install-prompt"
    >
      <div className="card-default p-4 shadow-2xl border-primary/40 bg-card/95 backdrop-blur">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-md bg-primary text-primary-foreground grid place-items-center shrink-0">
            <Download className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="label-overline text-primary">Add to home screen</div>
            <div className="font-display font-bold text-base leading-tight mt-1">
              Install ServiceSell PRO&trade;
            </div>
            {isIos ? (
              <p className="text-xs text-muted-foreground mt-2 leading-relaxed">
                Tap <Share className="inline-block w-3.5 h-3.5 align-text-bottom" /> <strong>Share</strong>, then
                <strong> Add to Home Screen </strong>
                <Plus className="inline-block w-3.5 h-3.5 align-text-bottom" /> — opens like a native app and works offline.
              </p>
            ) : (
              <p className="text-xs text-muted-foreground mt-2 leading-relaxed">
                One-tap install — full-screen, faster, and pinned to your home screen for the workshop.
              </p>
            )}
            <div className="flex gap-2 mt-3">
              {!isIos && (
                <button
                  type="button"
                  onClick={install}
                  className="btn-primary text-xs"
                  data-testid="pwa-install-btn"
                >
                  Install
                </button>
              )}
              <button
                type="button"
                onClick={dismiss}
                className="btn-outline text-xs"
                data-testid="pwa-install-dismiss-btn"
              >
                Maybe later
              </button>
            </div>
          </div>
          <button
            type="button"
            onClick={dismiss}
            aria-label="Dismiss install prompt"
            className="text-muted-foreground hover:text-foreground"
            data-testid="pwa-install-close-btn"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
